import React, { useState, useRef } from 'react';
import s3Service from '../services/s3Service';
import { OFFLINE_PHOTO_PREFIX, isQuotaError } from '../lib/offlineDb';

// Files larger than this are not buffered offline (protects device storage /
// iOS IndexedDB limits). The full `maxSize` still applies when online.
const OFFLINE_MAX_SIZE = 25 * 1024 * 1024; // 25MB

const FileUpload = ({
  onChange,
  disabled = false,
  accept = "*/*",
  maxSize = 100 * 1024 * 1024, // 100MB default
  folder = "documents",
  className = "",
  placeholder = "Choose file...",
  existingFile = null,
  existingFileName = '',
  // Optional. async (file, { folder }) => placeholderKey. When provided, a file
  // chosen while offline (or after an online upload fails) is buffered locally
  // for later sync instead of being lost. Files over OFFLINE_MAX_SIZE are
  // rejected offline. May throw if local storage is unavailable/full.
  onOfflineSave = null,
}) => {
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [fileName, setFileName] = useState('');
  const [fileUrl, setFileUrl] = useState(null);
  const fileInputRef = useRef(null);
  
  // Generate unique ID for this FileUpload instance
  const uniqueId = React.useMemo(() => `file-upload-${Math.random().toString(36).substr(2, 9)}`, []);

  // Generate presigned URL for existing file and set fileName
  React.useEffect(() => {
    console.log(`🔍 [FileUpload] useEffect triggered with:`, {
      existingFile,
      existingFileName,
      currentFileName: fileName
    });
    
    // Offline-buffered file (not yet uploaded): show a "saved offline" state,
    // don't try to fetch a presigned URL for a placeholder.
    if (
      typeof existingFile === 'string' &&
      existingFile.startsWith(OFFLINE_PHOTO_PREFIX)
    ) {
      // existingFileName is derived from the placeholder string, so ignore it
      // and show a clean "saved offline" label until sync replaces the value.
      setFileName('Saved offline — will upload');
      setFileUrl(null);
      return;
    }

    if (existingFile) {
      // Use presigned URL from backend instead of CDN URL
      const fetchPresignedUrl = async () => {
        try {
          // If existingFile is already a presigned URL (starts with http), use it directly
          if (typeof existingFile === 'string' && (existingFile.startsWith('http://') || existingFile.startsWith('https://'))) {
            console.log(`✅ [FileUpload] Using existing presigned URL:`, existingFile.substring(0, 50) + '...');
            setFileUrl(existingFile);
            if (existingFileName) {
              setFileName(existingFileName);
            }
            return;
          }
          
          // Otherwise, get presigned URL from backend
          const baseUrl = process.env.REACT_APP_API_URL || 'http://localhost:3001';
          const encodedKey = encodeURIComponent(existingFile);
          const response = await fetch(`${baseUrl}/api/s3/url/${encodedKey}`, {
            headers: {
              'Authorization': `Bearer ${localStorage.getItem('token')}`
            }
          });
          
          if (!response.ok) {
            throw new Error(`Failed to get presigned URL: ${response.statusText}`);
          }
          
          const data = await response.json();
          if (data.url) {
            console.log(`✅ [FileUpload] Generated presigned URL for existing file:`, data.url.substring(0, 50) + '...');
            setFileUrl(data.url);
          } else {
            console.error(`❌ [FileUpload] No URL in response from backend:`, data);
            // Don't fallback to CDN URL - show error instead
            setFileUrl(null);
            console.error(`❌ [FileUpload] Failed to get presigned URL for: ${existingFile}`);
          }
          
          // Set fileName from existingFileName
          if (existingFileName) {
            setFileName(existingFileName);
            console.log(`🔍 [FileUpload] Set fileName to:`, existingFileName);
          }
        } catch (error) {
          console.error(`❌ [FileUpload] Error getting presigned URL:`, error);
          console.error(`❌ [FileUpload] Error details:`, {
            message: error.message,
            existingFile: existingFile,
            stack: error.stack
          });
          // Don't fallback to CDN URL - show error instead
          setFileUrl(null);
          console.error(`❌ [FileUpload] Failed to get presigned URL for: ${existingFile}`);
          if (existingFileName) {
            setFileName(existingFileName);
          }
        }
      };
      
      fetchPresignedUrl();
    } else {
      setFileUrl(null);
      // Clear fileName if no existing file
      if (!fileName || fileName === existingFileName) {
        setFileName('');
        console.log(`🔍 [FileUpload] Cleared fileName`);
      }
    }
  }, [existingFile, existingFileName]);

  const handleFileChange = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Validate file type if accept is specified and includes image/*
    if (accept && accept.includes('image/*')) {
      if (!file.type.startsWith('image/')) {
        alert('Please select an image file (JPG, PNG, GIF, etc.)');
        return;
      }
    }

    // Validate file size
    if (file.size > maxSize) {
      alert(`File size must be less than ${Math.round(maxSize / (1024 * 1024))}MB`);
      return;
    }

    const canBufferOffline = typeof onOfflineSave === 'function';
    const isOnline = typeof navigator === 'undefined' ? true : navigator.onLine;

    // Buffer the file locally for later sync. Returns true on success, false if
    // the capture had to be aborted (too large for offline, or storage failure).
    const bufferOffline = async () => {
      if (file.size > OFFLINE_MAX_SIZE) {
        alert(
          `While offline, files must be under ${Math.round(
            OFFLINE_MAX_SIZE / (1024 * 1024)
          )}MB. Upload larger files when you’re back online.`
        );
        return false;
      }
      setFileName(file.name);
      try {
        const placeholder = await onOfflineSave(file, { folder });
        if (onChange) onChange(placeholder);
        return true;
      } catch (err) {
        setFileName('');
        alert(
          isQuotaError(err)
            ? 'This device is out of storage, so the file could not be saved for offline upload. Free up space and try again.'
            : 'Offline storage is unavailable, so the file cannot be saved while offline. Keep this tab open and try again once you are back online.'
        );
        return false;
      }
    };

    // Offline (or no connectivity reported): buffer straight away.
    if (!isOnline && canBufferOffline) {
      setUploading(true);
      try {
        await bufferOffline();
      } finally {
        setUploading(false);
      }
      return;
    }

    setUploading(true);
    setUploadProgress(0);
    setFileName(file.name);

    try {
      // Upload to S3 using presigned URL
      const s3Key = await s3Service.uploadDocument(file, {
        folder: folder,
        maxSize: maxSize,
        onProgress: (progress) => {
          setUploadProgress(progress);
        }
      });

      console.log(`✅ [S3 UPLOAD] Successfully uploaded file to S3:`, s3Key);

      // Call onChange with S3 key instead of file object
      if (onChange) {
        onChange(s3Key);
      }

    } catch (error) {
      console.error(`❌ [S3 UPLOAD] Failed to upload file:`, error);
      if (canBufferOffline) {
        // Online attempt failed (network/timeout) — buffer for later sync.
        await bufferOffline();
      } else if (onChange) {
        // Legacy fallback for screens without offline support: keep the File.
        onChange(file);
      }
    } finally {
      setUploading(false);
      setUploadProgress(0);
    }
  };

  const handleRemove = () => {
    setFileName('');
    setFileUrl(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
    if (onChange) {
      onChange(null);
    }
  };

  // Function to shorten file name for display
  const getShortFileName = (fullName) => {
    if (!fullName) return '';
    
    // If filename is longer than 25 characters, show first 15 + ... + last 5
    if (fullName.length > 25) {
      const extension = fullName.split('.').pop();
      const nameWithoutExt = fullName.substring(0, fullName.lastIndexOf('.'));
      const shortName = nameWithoutExt.substring(0, 15) + '...' + nameWithoutExt.substring(nameWithoutExt.length - 5);
      return shortName + '.' + extension;
    }
    
    return fullName;
  };

  // Debug: Log render state
  console.log(`🔍 [FileUpload] Rendering with state:`, {
    fileName,
    fileUrl,
    existingFile,
    existingFileName,
    uploading
  });

  return (
    <div className={`file-upload ${className}`}>
      <input
        ref={fileInputRef}
        type="file"
        accept={accept}
        onChange={handleFileChange}
        disabled={disabled || uploading}
        style={{ display: 'none' }}
        id={uniqueId}
      />
      
      {fileName ? (
        <div className="file-info">
          <div className="file-details">
            <span className="file-name" title={fileName}>
              {getShortFileName(fileName)}
            </span>
            {fileUrl ? (
              <a 
                href={fileUrl} 
                target="_blank" 
                rel="noopener noreferrer"
                className="download-link"
                title={`Download ${fileName}`}
                onClick={(e) => {
                  // Ensure we're using presigned URL (not CDN)
                  if (fileUrl && !fileUrl.includes('cloudfront.net') && !fileUrl.includes('amazonaws.com')) {
                    console.log(`⚠️ [FileUpload] Download link may not be a presigned URL:`, fileUrl.substring(0, 50));
                  }
                  console.log(`✅ [FileUpload] Downloading file using presigned URL:`, fileUrl.substring(0, 50) + '...');
                }}
              >
                📥
              </a>
            ) : (
              <span 
                className="download-link" 
                style={{ opacity: 0.5, cursor: 'not-allowed' }}
                title="File URL not available"
              >
                📥
              </span>
            )}
          </div>
          {!uploading && (
            <button
              type="button"
              onClick={handleRemove}
              className="remove-file-btn"
              title={`Remove ${fileName}`}
            >
              ×
            </button>
          )}
        </div>
      ) : (
        <label 
          htmlFor={uniqueId} 
          className={`file-upload-label ${disabled || uploading ? 'disabled' : ''}`}
        >
          {uploading ? (
            <div className="upload-progress">
              <span>Uploading... {uploadProgress}%</span>
              <div className="progress-bar">
                <div 
                  className="progress-fill" 
                  style={{ width: `${uploadProgress}%` }}
                ></div>
              </div>
            </div>
          ) : (
            placeholder
          )}
        </label>
      )}
      
      <style jsx>{`
        .file-upload {
          width: 100%;
        }
        
        .file-info {
          display: flex;
          align-items: center;
          justify-content: space-between;
          padding: 6px 8px;
          border: 1px solid #ddd;
          border-radius: 4px;
          background: #f9f9f9;
          font-size: 11px;
          min-height: 32px;
        }
        
        .file-details {
          display: flex;
          align-items: center;
          flex: 1;
          margin-right: 6px;
          min-width: 0; /* Allow flex item to shrink below content size */
        }
        
        .file-name {
          color: #333;
          flex: 1;
          margin-right: 6px;
          overflow: hidden;
          text-overflow: ellipsis;
          white-space: nowrap;
          min-width: 0; /* Allow text to be truncated */
        }
        
        .download-link {
          color: #0a7280;
          text-decoration: none;
          font-size: 12px;
          margin-left: 4px;
          flex-shrink: 0; /* Prevent download button from shrinking */
          padding: 2px;
        }
        
        .download-link:hover {
          color: #0a5a66;
          background: rgba(10, 114, 128, 0.1);
          border-radius: 2px;
        }
        
        .remove-file-btn {
          background: #dc3545;
          color: white;
          border: none;
          border-radius: 50%;
          width: 18px;
          height: 18px;
          display: flex;
          align-items: center;
          justify-content: center;
          cursor: pointer;
          font-size: 11px;
          line-height: 1;
          flex-shrink: 0; /* Prevent remove button from shrinking */
          margin-left: 4px;
        }
        
        .remove-file-btn:hover {
          background: #c82333;
          transform: scale(1.1);
        }
        
        .file-upload-label {
          display: block;
          padding: 8px 12px;
          border: 1px solid #ddd;
          border-radius: 4px;
          background: #f9f9f9;
          cursor: pointer;
          font-size: 12px;
          color: #666;
          text-align: center;
          transition: border-color 0.3s;
        }
        
        .file-upload-label:hover:not(.disabled) {
          border-color: #0a7280;
        }
        
        .file-upload-label.disabled {
          cursor: not-allowed;
          opacity: 0.6;
        }
        
        .upload-progress {
          display: flex;
          flex-direction: column;
          align-items: center;
          gap: 4px;
        }
        
        .progress-bar {
          width: 100%;
          height: 4px;
          background-color: #e0e0e0;
          border-radius: 2px;
          overflow: hidden;
        }
        
        .progress-fill {
          height: 100%;
          background-color: #0a7280;
          border-radius: 2px;
          transition: width 0.3s ease;
        }
      `}</style>
    </div>
  );
};

export default FileUpload;
